import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { createMessageStyles } from '@core/UI/styles/Message.styles';
import { useTheme } from '@core/UI/context/ThemeContext';
import { getImageAsset } from '@core/UI/utils/getAssetHelper';
import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import {
  useT,
  formatAmount,
  formatCurrencyUnit,
  formatMultiplier,
} from '@core/i18n';

export type MessageStyleResolver = (
  gameSlug?: string | null
) => Record<string, React.CSSProperties> | null;

export type CashoutMessageStyleResolver = MessageStyleResolver;

export type ErrorMessageStyleResolver = MessageStyleResolver;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsMobile(w <= 768 || Math.min(w, h) <= 480);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

const useIsPortrait = () => {
  const [isPortrait, setIsPortrait] = useState(
    window.innerHeight >= window.innerWidth
  );
  useEffect(() => {
    const onResize = () =>
      setIsPortrait(window.innerHeight >= window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isPortrait;
};

function isInsufficientBalanceMessage(
  message: string | undefined,
  localizedLabels: readonly string[]
): boolean {
  if (!message) return false;
  const normalized = message.trim().toLowerCase();
  if (
    localizedLabels.some(
      label => !!label && label.trim().toLowerCase() === normalized
    )
  ) {
    return true;
  }
  // Hardcoded AutoPlayService / English fallbacks
  return /insufficient balance/i.test(message);
}

export interface NotificationMessage {
  id: number;
  type: 'success' | 'error';
  message?: string;
  multiplier?: number;
  winAmount?: number;
  currency?: string;
  betSide?: 'A' | 'B';

  isExpiring?: boolean;
}

interface MessageItemProps {
  notification: NotificationMessage;
  onClose: (id: number) => void;
  winStarUrl: string;
  gameSlug: string;
  cashoutMessageStyles: Record<string, React.CSSProperties> | null;
  errorMessageStyles: Record<string, React.CSSProperties> | null;
}

const MessageItem: React.FC<MessageItemProps> = ({
  notification,
  onClose,
  winStarUrl,
  gameSlug: _gameSlug,
  cashoutMessageStyles,
  errorMessageStyles,
}) => {
  const { theme } = useTheme();
  const t = useT(isSkyxNewRoute(_gameSlug) ? 'skyx_new' : undefined);
  const isMobile = useIsMobile();
  const isPortrait = useIsPortrait();
  const defaultStyles = useMemo(() => createMessageStyles(theme), [theme]);
  const successMessageStyles = cashoutMessageStyles ?? defaultStyles;
  // Always use the red default error toast for insufficient balance in every
  // language — never the purple Sky-X error palette (other errors keep that).
  const insufficientBalanceLabels = useMemo(
    () => [
      t('ui.messages.error_insufficient_balance'),
      t('ui.popuppanel.autoplay.error_insufficient_balance'),
      'Insufficient balance',
      'Insufficient balance to continue Auto Play',
    ],
    [t]
  );
  const errorToastStyles = isInsufficientBalanceMessage(
    notification.message,
    insufficientBalanceLabels
  )
    ? defaultStyles
    : (errorMessageStyles ?? defaultStyles);
  const currency = notification.currency || 'USD';
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsMounted(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const isVisible = isMounted && !notification.isExpiring;
  const isSkyxMobileCashout =
    notification.type === 'success' &&
    Boolean(cashoutMessageStyles) &&
    isSkyxNewRoute(_gameSlug) &&
    isMobile;

  const messageStyles =
    notification.type === 'success' ? successMessageStyles : errorToastStyles;

  const dynamicStyle: React.CSSProperties = {
    ...messageStyles.messageBox,
    ...(notification.type === 'success'
      ? messageStyles.messageSuccess
      : messageStyles.messageError),
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
    transition: 'opacity 0.5s ease, transform 0.5s ease',
    ...(isSkyxMobileCashout
      ? {
          width: 'min(360px, calc(100vw - 24px))',
          maxWidth: 'calc(100vw - 24px)',
          marginBottom: isPortrait ? '8px' : '4px',
          overflow: 'visible',
        }
      : {}),
  };

  const handleClose = () => {
    onClose(notification.id);
  };

  if (notification.type === 'success' && cashoutMessageStyles) {
    const closeDefaultColor = cashoutMessageStyles.messageCloseButton
      ?.color as string;

    return (
      <div style={dynamicStyle}>
        <div style={cashoutMessageStyles.messageSuccessLeft}>
          <div style={cashoutMessageStyles.messageSuccessLeftLabel}>
            {t('ui.control_panel.action.you_have_cashed_out')}
          </div>
          <div style={cashoutMessageStyles.messageSuccessLeftMultiplier}>
            {formatMultiplier(notification.multiplier, currency)}
          </div>
        </div>
        <div style={cashoutMessageStyles.messageSuccessCenter}>
          <div
            style={{
              ...cashoutMessageStyles.messageStar,
              ...cashoutMessageStyles.messageStarLeft,
              backgroundImage: `url('${winStarUrl}')`,
            }}
          />
          <div
            style={{
              ...cashoutMessageStyles.messageStar,
              ...cashoutMessageStyles.messageStarRight,
              backgroundImage: `url('${winStarUrl}')`,
            }}
          />
          <div style={cashoutMessageStyles.messageSuccessWinLabel}>
            {t('ui.hud.win_currency', {
              currency: formatCurrencyUnit(currency),
            })}
          </div>
          <div style={cashoutMessageStyles.messageSuccessWinAmount}>
            {formatAmount(notification.winAmount, currency)}
          </div>
        </div>
        <button
          style={cashoutMessageStyles.messageCloseButton}
          onClick={handleClose}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#FFFFFF';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = closeDefaultColor;
          }}
        >
          &times;
        </button>
      </div>
    );
  }

  if (notification.type === 'success') {
    return (
      <div style={dynamicStyle}>
        <div style={messageStyles.messageSuccessLeft}>
          <div
            style={{
              fontSize: '12px',
              color: '#9ea0a3',
              lineHeight: '12px',
            }}
          >
            {t('ui.control_panel.action.you_have_cashed_out')}
          </div>
          <div
            style={{
              fontSize: '20px',
              lineHeight: '20px',
              marginTop: '3px',
              color: 'white',
            }}
          >
            {formatMultiplier(notification.multiplier, currency)}
          </div>
        </div>
        <div style={messageStyles.messageSuccessCenter}>
          <div
            style={{
              position: 'absolute',
              left: '1px',
              background: `url('${winStarUrl}')`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              width: '31px',
              height: '31px',
              transform: 'rotateY(180deg)',
              opacity: 0.5,
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '1px',
              background: `url('${winStarUrl}')`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              width: '31px',
              height: '31px',
              opacity: 0.5,
            }}
          />
          <div
            style={{
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '0px',
            }}
          >
            {t('ui.hud.win_currency', {
              currency: formatCurrencyUnit(currency),
            })}
          </div>
          <div
            style={{
              fontSize: isMobile ? '20px' : '18px',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            {formatAmount(notification.winAmount, currency)}
          </div>
        </div>
        <button
          style={messageStyles.messageCloseButton}
          onClick={handleClose}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#FFff';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = '#97a4ae';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          &times;
        </button>
      </div>
    );
  }

  const isMobilePortrait = isMobile && isPortrait;
  const errorStyle: React.CSSProperties = isMobilePortrait
    ? {
        maxWidth: 'calc(100vw - 24px)',
        padding: '6px 12px',
        fontSize: '16px',
      }
    : isMobile
      ? {
          maxWidth: 'calc(100vw - 40px)',
          padding: '6px 16px',
          fontSize: '14px',
        }
      : { maxWidth: '500px' };

  const closeDefaultColor = messageStyles.messageCloseButton?.color as
    | string
    | undefined;

  return (
    <div style={{ ...dynamicStyle, ...errorStyle }}>
      <span
        style={{
          flex: 1,
          overflow: isMobilePortrait ? 'visible' : 'hidden',
          textOverflow: isMobilePortrait ? 'clip' : 'ellipsis',
          whiteSpace: isMobilePortrait ? 'normal' : 'nowrap',
          wordBreak: isMobilePortrait ? 'break-word' : 'normal',
        }}
      >
        {notification.message}
      </span>
      <button
        style={{
          ...messageStyles.messageCloseButton,
          marginLeft: '12px',
          fontSize: isMobilePortrait ? '20px' : isMobile ? '24px' : '32px',
          padding: isMobilePortrait
            ? '2px 6px'
            : isMobile
              ? '4px 8px'
              : '8px 12px',
          flexShrink: 0,
        }}
        onClick={handleClose}
        onMouseEnter={e => {
          e.currentTarget.style.color = errorMessageStyles
            ? '#FFFFFF'
            : '#FFff';
          e.currentTarget.style.transform = 'scale(1)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.color = closeDefaultColor ?? '#97a4ae';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        &times;
      </button>
    </div>
  );
};

interface MessagesProps {
  notifications: NotificationMessage[];
  onCloseNotification: (id: number) => void;
  gameSlug?: string;
  resolveCashoutMessageStyles?: CashoutMessageStyleResolver;
  resolveErrorMessageStyles?: ErrorMessageStyleResolver;
}

export const Messages: React.FC<MessagesProps> = ({
  notifications,
  onCloseNotification,
  gameSlug = 'crash',
  resolveCashoutMessageStyles,
  resolveErrorMessageStyles,
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const isPortrait = useIsPortrait();
  const isSkyxNew = isSkyxNewRoute(gameSlug);
  const isSkyxMobile = isSkyxNew && isMobile;
  const messageStyles = useMemo(() => createMessageStyles(theme), [theme]);
  const cashoutMessageStyles = useMemo(
    () => resolveCashoutMessageStyles?.(gameSlug) ?? null,
    [gameSlug, resolveCashoutMessageStyles]
  );
  const errorMessageStyles = useMemo(
    () => resolveErrorMessageStyles?.(gameSlug) ?? null,
    [gameSlug, resolveErrorMessageStyles]
  );

  const winStarUrl = useMemo(() => {
    const assetPath = getImageAsset(gameSlug, 'winStar');
    const folder = resolveAssetFolderSlug(gameSlug) || gameSlug;
    return buildGamePublicAssetUrl(folder, assetPath);
  }, [gameSlug]);

  const containerStyle: React.CSSProperties = {
    ...messageStyles.messagesContainer,
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 99999,
    padding: '0 16px',
    boxSizing: 'border-box' as const,
    top: isSkyxMobile
      ? isPortrait
        ? 'calc(var(--headerHeightDynamic, 40px) + var(--resultHistoryHeight, 42px) + 5px)'
        : 'calc(var(--headerHeightDynamic, 40px) + var(--resultHistoryHeight, 32px) + 5px)'
      : 'calc(env(safe-area-inset-top, 0px) + 45px)',
    ...(isSkyxMobile
      ? {
          width: 'auto',
          maxWidth: 'calc(100vw - 24px)',
          overflow: 'visible',
        }
      : isMobile
        ? {
            width: '100%',
            maxWidth: '92vw',
          }
        : {
            width: 'auto',
            maxWidth: isSkyxNew ? '420px' : '500px',
          }),
  };

  const container = (
    <div style={containerStyle}>
      {notifications.map(notif => (
        <MessageItem
          key={notif.id}
          notification={notif}
          onClose={onCloseNotification}
          winStarUrl={winStarUrl}
          gameSlug={gameSlug}
          cashoutMessageStyles={cashoutMessageStyles}
          errorMessageStyles={errorMessageStyles}
        />
      ))}
    </div>
  );

  // #gamehud (z-index 3) sits under #root/header (z-index 25) in sky-x handheld
  // landscape, so the toast must escape it to stay above the header.
  if (isSkyxNew && typeof document !== 'undefined') {
    return createPortal(container, document.body);
  }

  return container;
};
